/**
 * AssetSource for the in-browser TEST runner.
 *
 * Serves the current session assetStore — the same virtual /assets the live
 * runtime preloads — optionally overlaid by `TestAssetRef`s carried on a
 * `test:request` payload (a later pipeline task sends those; refs WIN by
 * path). A ref's bytes travel inline as base64, or by reference: a direct
 * `url`, else the backend's content-addressed endpoint
 * (`GET /api/assets/:hash?type=<mime>&content=true`).
 *
 * With zero session assets and no refs the manifest is empty, so the test
 * runner's unconditional `AssetManager.__init(createTestAssetSource())` is a
 * no-op for asset-less games.
 */
import type { AssetSource, AssetEntry, LoadedAsset } from '@gotti/core';
import type { TestAssetRef } from '@gotti/sandbox-core';
import { listAssets, getAsset, base64ToBytes } from './assetStore';
import { bytesToLoadedAsset, fetchAssetContentBytes } from './assetDecode';
import { stripClientNamespace } from './browserAssetSource';

function fetchRefBytes(ref: TestAssetRef): Promise<Uint8Array> {
  return fetchAssetContentBytes(ref, 'createTestAssetSource');
}

export function createTestAssetSource(assets?: TestAssetRef[]): AssetSource {
  const overlay = new Map<string, TestAssetRef>();
  for (const ref of assets ?? []) overlay.set(ref.path, ref);
  return {
    manifest(): AssetEntry[] {
      const entries = new Map<string, AssetEntry>();
      for (const a of listAssets()) {
        entries.set(a.path, { path: a.path, mimeType: a.mimeType, size: a.size, hash: a.hash });
      }
      // Refs WIN by path — a test:request can pin newer bytes over the session store.
      for (const ref of overlay.values()) {
        entries.set(ref.path, { path: ref.path, mimeType: ref.mimeType, size: ref.size, hash: ref.hash });
      }
      return [...entries.values()];
    },
    async load(path: string): Promise<LoadedAsset> {
      // The manager loads with the full namespaced path; refs + the session
      // store are namespace-less, so strip a leading `client/` before matching.
      const rel = stripClientNamespace(path);
      const ref = overlay.get(rel);
      if (ref) {
        const bytes = ref.base64 !== undefined ? base64ToBytes(ref.base64) : await fetchRefBytes(ref);
        return bytesToLoadedAsset(path, ref.mimeType, bytes);
      }
      const a = getAsset(rel);
      if (!a) throw new Error(`createTestAssetSource: no asset "${path}"`);
      return bytesToLoadedAsset(path, a.mimeType, base64ToBytes(a.base64));
    },
  };
}
